/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import useSWR from "swr"
import { useRouter } from "next/router"
import { useAuthReady } from "./useAuthReady"
import {
  ResponseShopPaymentPointGetList,
  ResponseShopPaymentPointGetOne,
  ShopPaymentPoint,
} from "../libs/api/backend/src"
import {
  UrlShopPaymentPointFindListAPI,
  UrlShopPaymentPointFindOneAPI,
} from "../libs/api/url"

export function useShopPaymentPointId(): string {
  let shopPaymentPointId = ""

  try {
    const router = useRouter()
    shopPaymentPointId = (router.query.shopPaymentPointId as string) || ""
  } catch (err) {
    console.error(err)
  }

  return shopPaymentPointId
}

export function useShopPaymentPointFindOne(
  enterpriseId?: string,
  shopPaymentPointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopPaymentPointGetOne>(
    isAuthenticated && enterpriseId && shopPaymentPointId
      ? UrlShopPaymentPointFindOneAPI(enterpriseId, shopPaymentPointId)
      : null
  )

  const shopPaymentPoint = apiResponse && (apiResponse.data as ShopPaymentPoint)

  return {
    shopPaymentPoint: apiResponse && shopPaymentPoint ? shopPaymentPoint : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopPaymentPointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopPaymentPointFindAll(
  enterpriseId?: string,
  {
    shopId,
    shopSalePointId,
  }: {
    shopId?: string
    shopSalePointId?: string
  } = {}
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopPaymentPointGetList>(
    isAuthenticated && enterpriseId
      ? UrlShopPaymentPointFindListAPI(enterpriseId, shopId, shopSalePointId)
      : null
  )

  const shopPaymentPoints = apiResponse && apiResponse.data

  return {
    shopPaymentPoints:
      apiResponse && shopPaymentPoints ? shopPaymentPoints : null,
    isLoading: Boolean(
      authLoading || (isAuthenticated && enterpriseId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopPaymentPointFindAllForShopID(
  enterpriseId?: string,
  shopId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopPaymentPointGetList>(
    isAuthenticated && enterpriseId && shopId
      ? UrlShopPaymentPointFindListAPI(enterpriseId, shopId, undefined)
      : null
  )

  const shopPaymentPoints = apiResponse && apiResponse.data

  return {
    shopPaymentPoints:
      apiResponse && shopPaymentPoints ? shopPaymentPoints : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && shopId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useShopPaymentPointFindAllForSalePointID(
  enterpriseId?: string,
  shopSalePointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseShopPaymentPointGetList>(
    isAuthenticated && enterpriseId && shopSalePointId
      ? UrlShopPaymentPointFindListAPI(enterpriseId, undefined, shopSalePointId)
      : null
  )

  const shopPaymentPoints =
    apiResponse && (apiResponse.data as Array<ShopPaymentPoint>)

  return {
    shopPaymentPoints:
      apiResponse && shopPaymentPoints ? shopPaymentPoints : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopSalePointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}
